<template>
  <div id="hot-paste-wrapper">
    <el-row class="paste-header">
      <el-col :span="12">
        <p>Paste in values from a spreadsheet.</p>
        <p>The first row must contain column headers, e.g. Account, Amount, etc.</p>
      </el-col>
      <el-col :span="12" class="paste-buttons">
        <ul>
          <li>Lines: {{ validatedLines.length }}</li>
          <li>Amount: {{ total_decimal }}</li>
        </ul>
        <el-button class="m-right-5" type="danger" @click="resetTable()" tabindex="-1">Clear</el-button>
        <el-button class="m-right-5" type="default" @click="validate()" tabindex="-1">Validate</el-button>
        <el-button type="primary" :disabled="!validated" @click="acceptValidatedLines" tabindex="-1">Import</el-button>
      </el-col>
    </el-row>
    <el-row>
      <el-col :span="24">
        <el-radio-group v-model="selectedImport" style="float: right;">
          <el-radio-button label="add">Add</el-radio-button>
          <el-radio-button label="replace">Replace</el-radio-button>
        </el-radio-group>
      </el-col>
    </el-row>
    <el-dialog
      title="Message"
      :visible.sync="showDialog"
      size="tiny"
      id="hot-dialog"
      ref="hot-dialog"
      top="5%"
      z-index="1"
      :close-on-click-modal="true"
      :close-on-press-escape="true">
        {{ dialogMessage }}
    </el-dialog>
    <div class="clear"></div>
    <div class="m-top-20">
      <div id="hot-paste" ref="hot-paste"></div>
    </div>
  </div>
</template>

<script>
  /* global Handsontable */
  // import { mapGetters } from 'vuex'

  export default {
    name: 'hotPaste',
    props: {
      dialogSize: Object,
      entityType: String
    },
    data () {
      return {
        validated: false,
        // allCodes: undefined, // this is where we will compare values against system codes
        hotObject: null,  // This is the HOT object
        // See HOT documentation for all options
        hotProperties: {
          data: [ [ 'Paste Here' ] ],
          cells: function (row, col, prop) {
            var cellProperties = {}
            if (row === 0) {
              cellProperties.className = 'bold' // uses function directly
            }
            return cellProperties
          },
          afterChange: (changes, source) => {
            // We don't want a fresh load of data to be marked as all modified
            if (source === 'loadData') return true

            // Get position and values of modified data
            const row = changes[0][0]
            const col = changes[0][1]

            /*
            // In case we need this in the future
            const oldValue = changes[0][2]
            const newValue = changes[0][3]
            */

            // Mark the row as modified, but need to keep the exiting error highlighting
            this.modified.push({ row: row, col: col, className: 'modified' })
            this.validated = false
            const highlights = this.cellErrors.concat(this.modified)
            this.hotObject.updateSettings({
              cell: highlights
            })

            return true
          },
          width: (this.dialogSize.width) ? (this.dialogSize.width * 0.95) : 0,
          height: (this.dialogSize.width) ? (this.dialogSize.height - 15) : 0,
          stretchH: 'last',
          minCols: 6,
          minRows: 20,
          rowHeaders: true,
          columnSorting: false,
          filters: false,
          dropdownMenu: true,
          contextMenu: false,
          autoRowSize: true,
          autoColumnSize: true,
          allowRemoveRow: false,
          allowRemoveColumn: false,
          manualColumnMove: false,
          manualRowMove: false
        },
        validatedLines: [],   // validated data against codes here
        counters: {
          debits_decimal: 0,
          credits_decimal: 0,
          total_decimal: 0
        },
        convertedObject: [],  // This is our final object to send back to Sigma that matches data of DistributionLines component
        cellErrors: [],       // array of errors to be used for HOT cell highlighting
        modified: [],         // array of modified cells to be used for HOT cell highlighting
        // Dialog box stuff
        showDialog: false,
        dialogMessage: '',
        fieldProperties: {
          /*
          fieldtype: {
            required: boolean,  // true = column is required and will cause validation errors if missing
            blank: boolean,  // true = field can be blank, e.g. Free fields
            allowedLabels: array of strings, // list of possible column headings. Will match the first occurence
            sigmaName: string, // name of field in Sigma forms. Needed for conversion
            column: number // index of field. -1 = column data is not yet defined/missing
          }
          */
          accounts: {
            required: true,
            blank: false,
            allowedLabels: [ 'account', 'accounts', 'acct' ],
            sigmaName: 'account',
            column: -1
          },
          item: {
            required: (this.entityType === 'ar'),
            blank: false,
            allowedLabels: [ 'item', 'billable item' ],
            sigmaName: 'item',
            column: -1
          },
          markup_type: {
            required: (this.entityType === 'ar'),
            blank: false,
            allowedLabels: [ 'markup type', 'markuptype' ],
            sigmaName: 'markup_type',
            column: -1
          },
          markup_amount: {
            required: (this.entityType === 'ar'),
            blank: false,
            allowedLabels: [ 'markup', 'markup amount', 'markup percentage' ],
            sigmaName: 'markup_amount',
            column: -1
          },
          series: {
            required: true,
            blank: false,
            allowedLabels: [ 'series', 'ser', 'episodes', 'episode' ],
            sigmaName: 'series',
            column: -1,
            active: true
          },
          locations: {
            required: true,
            blank: false,
            allowedLabels: [ 'locations', 'location', 'locs', 'loc' ],
            sigmaName: 'location',
            column: -1,
            active: true
          },
          sets: {
            required: true,
            blank: false,
            allowedLabels: [ 'sets', 'set', 'st' ],
            sigmaName: 'set',
            column: -1,
            active: true
          },
          taxcodes: {
            required: false,
            blank: true,
            allowedLabels: [ 'tax codes', 'tax code', 'taxcode', 'tax', '9', '1099', 'vat' ],
            sigmaName: 'tax_code',
            column: -1
          },
          freefield1: {
            required: false,
            blank: true,
            allowedLabels: [ 'ff1', 'ff 1', 'free', 'free field', 'free field 1', 'freefield1' ],
            sigmaName: 'free_field_1',
            column: -1
          },
          freefield2: {
            required: false,
            blank: true,
            allowedLabels: [ 'ff2', 'ff 2', 'free field 2', 'freefield2' ],
            sigmaName: 'free_field_2',
            column: -1
          },
          freefield3: {
            required: false,
            blank: true,
            allowedLabels: [ 'ff3', 'ff 3', 'free', 'free field', 'free field 3', 'freefield3' ],
            sigmaName: 'free_field_3',
            column: -1
          },
          freefield4: {
            required: false,
            blank: true,
            allowedLabels: [ 'ff4', 'ff 4', 'free field 4', 'freefield4' ],
            sigmaName: 'free_field_4',
            column: -1
          },
          description: {
            required: true,
            blank: true,
            allowedLabels: [ 'description', 'desc', 'descriptions', 'line', 'line description' ],
            sigmaName: 'description',
            column: -1
          },
          other_name: {
            required: false,
            blank: true,
            allowedLabels: [ 'other_name', 'other name', 'othername' ],
            sigmaName: 'other_name',
            column: -1
          },
          amount: {
            required: true,
            blank: false,
            allowedLabels: [ 'amount', 'total', 'amounts', 'totals', '$' ],
            sigmaName: 'amount_decimal',
            column: -1
          },
          asset: {
            required: false,
            blank: true,
            allowedLabels: [ 'asset', 'assets' ],
            sigmaName: 'asset',
            column: -1
          },
          vendors: {
            required: false,
            blank: true,
            allowedLabels: [ 'third', '3rd', 'vendor', 'third_party_vendor', 'thirdparty', 'third_party', 'third party' ],
            sigmaName: 'third_party_vendor',
            column: -1
          }
          /* future
          other_name: {
            required: false,
            blank: true,
            allowedLabels: [ 'other name', 'othername', 'merchant', 'name', 'employee', 'employeename', 'employee name', 'employee_name' ],
            sigmaName: 'other_name',
            column: -1
          }
          */
        },
        selectedImport: 'add'
      }
    },
    computed: {
      total_decimal () { return (isNaN(this.counters.total_decimal)) ? '0.00' : this.counters.total_decimal.toFixed(2) },
      credits_decimal () { return (isNaN(this.counters.credits_decimal)) ? '0.00' : this.counters.credits_decimal.toFixed(2) },
      debits_decimal () { return (isNaN(this.counters.debits_decimal)) ? '0.00' : this.counters.debits_decimal.toFixed(2) },
      allCodes () {
        return {
          item: this.$store.getters.getAllBillableitem,
          accounts: this.$store.getters.getAllAccounts,
          freefield1: this.$store.getters.getAllFreefields,
          freefield2: this.$store.getters.getAllFreefields,
          freefield3: this.$store.getters.getAllFreefields,
          freefield4: this.$store.getters.getAllFreefields,
          locations: this.$store.getters.getAllLocations,
          series: this.$store.getters.getAllSeries,
          sets: this.$store.getters.getAllSets,
          taxcodes: this.$store.getters.getAllTaxcodes,
          vendors: undefined
        }
      }
    },
    methods: {
      // Trim whitespace from array(s) of data
      trimWhitespace (data) {
        if (!data) return false
        if (Array.isArray(data)) {
          return data.map((element) => {
            return this.trimWhitespace(element)
          })
        } else {
          return String(data).trim()
        }
      },
      // Create a HOT table on 'hot-paste' ref
      createTable () {
        let table = new Handsontable(this.$refs['hot-paste'], this.hotProperties)
        table.selectCell(0, 0) // Focus on top left cell
        return table
      },
      // Remove data from our object
      clearValidated () {
        this.validated = false
        this.cellErrors = []
        this.modified = []
        this.counters = { total_decimal: 0, debits_decimal: 0, credits_decimal: 0 }
        this.validatedLines = []
        this.convertedObject = []
      },
      // Destroy HOT and create a new one
      resetTable () {
        this.clearValidated()
        this.allCodes.vendors = undefined
        this.hotObject.clear()
        this.hotObject.destroy()
        this.hotObject = this.createTable()
      },
      // Simple Dialog box.  TODO.  Need to fix z-index and add buttons to accept/decline
      createDialog (message) {
        this.showDialog = true
        this.dialogMessage = message
      },
      createNotification (title, message, type) {
        this.$notify({
          title: title || 'Message',
          message: message,
          type: type || 'info'
        })
      },
      loadAccounts () {
        return new Promise((resolve, reject) => {
          let colAccount = this.fieldProperties.accounts.column
          if (colAccount >= 0) {
            // Get unique non-blank/null values
            let pastedAccounts = this.hotObject.getDataAtCol(colAccount).filter((val, ind, self) => val && self.indexOf(val) === ind)
            pastedAccounts.shift()  // remove header
            let accountPromises = []
            accountPromises.push(this.$store.dispatch('fetchAccountsWithQuery', { code__in: [...new Set(pastedAccounts)], page_size: pastedAccounts.length }))
            Promise.all(accountPromises)
            .then((responses) => {
              this.allCodes.accounts = this.$store.getters.getAllAccounts
              resolve(true)
            })
            .catch((e) => {
              console.log(e)
              reject(false)
            })
          } else {
            resolve(true)
          }
        })
      },
      loadVendors () {
        return new Promise((resolve, reject) => {
          // Load vendor data for comparison.  All other fields are currently cached
          let colVendor = this.fieldProperties.vendors.column
          if (colVendor >= 0) {
            // Get unique non-blank/null values
            let pastedVendors = this.hotObject.getDataAtCol(colVendor).filter((val, ind, self) => val && self.indexOf(val) === ind)
            pastedVendors.shift() // remove header

            let vendorPromises = []
            for (let i in pastedVendors) {
              vendorPromises.push(this.$store.dispatch('fetchVendorsWithQuery', { alias_name__icontains: pastedVendors[i] }))
            }

            Promise.all(vendorPromises)
            .then((responses) => {
              this.allCodes.vendors = this.$store.getters.getVendorsCache
              resolve(true)
            })
            .catch((e) => {
              console.log(e)
              reject(false)
            })
          } else {
            resolve(true)
          }
        })
      },
      // Figure out which columns contain our data
      buildFieldIndices (data) {
        const importData = data || this.hotObject.getData()
        const pastedHeaders = importData[0]
        // Loop through our valid field choices
        for (let field in this.fieldProperties) {
          let fieldData = this.fieldProperties[field]

          // Loop through possible field header combos and assign the column index to our properties
          for (let col in pastedHeaders) {
            const headerField = (pastedHeaders[col] || '').toLowerCase()
            if (fieldData.allowedLabels.includes(headerField)) {
              fieldData.column = col
              break
            }
          }

          // Exit if a required field is missing
          if (fieldData.required === true && fieldData.column === -1) {
            this.createNotification('Error', 'A required column is missing: ' + field, 'error')
            return false
          }
        }
      },
      // Check data against production values
      validate () {
        this.clearValidated()  // reset errors and previously validated data
        const importData = this.trimWhitespace(this.hotObject.getData())

        // Basic error checking to see if spreadsheet contains data and proper headers
        if (!importData || importData.length === 0 || this.buildFieldIndices(importData) === false) return false

        this.loadAccounts()
        .then(() => {
          this.loadVendors()
          .then(() => {
            // Loop through imported data and put errors and data into error and validatedData arrays, respectively
            // "row" in the loop repressents the row of the HOT data, and fieldIndex[field] is the column
            // Starting at index 1 to skip the header row
            for (let row = 1; row < importData.length; row++) {
              let rowData = importData[row]
              let finalLine = {} // Our new line with correct, validated and scrubbed values

              // Skip over data that isn't validated in
              if (this.hotObject.isEmptyRow(row)) continue

              for (let type in this.fieldProperties) {
                const column = this.fieldProperties[type].column
                // Skip over data that isn't pasted in
                if (column < 0) continue

                // Get the object metadata or at least scrub it
                const scrubbedField = this.checkCode(type, rowData[column])

                // Mark cells as errors
                // False is an error, undefined or blanks are if the "blank" property is true
                if (scrubbedField === false) {
                  this.cellErrors.push({ row: row, col: column, className: 'cell-error' })  // Blank, but are blanks allowed?
                } else if (!scrubbedField && this.fieldProperties[type].blank === false) {
                  this.cellErrors.push({ row: row, col: column, className: 'cell-error' })
                } else if (scrubbedField && !scrubbedField.active && !!this.fieldProperties[type].active) { // Allow only active field
                  this.cellErrors.push({ row: row, col: column, className: 'cell-error' })
                }
                finalLine[type] = scrubbedField
              }
              this.validatedLines.push(finalLine)
            }
            this.showCellErrors()         // Display errors if any
            if (this.cellErrors.length > 0) {
              this.createNotification('Error', 'Please correct errors before continuing', 'error')
              return false
            } else {
              this.convertToLineObject()  // change to sigma form for easier ingestion
              this.createNotification('Success', 'Data is valid. "Import" to continue.', 'success')
              this.validated = true
              return true
            }
          })
        })
      },
      // Helper function to scrub/check field data
      checkCode (type, rawField) {
        if (!rawField || typeof this.allCodes !== 'object') return undefined
        let matchedData

        // if amount, make sure it's a decimal
        if (type === 'amount') {
          const amount = parseFloat(rawField.replace(/[^0-9.-]/g, ''))
          matchedData = amount.toFixed(2)
          // update our counters
          this.counters.total_decimal += amount
          if (rawField >= 0) this.counters.debits_decimal += amount
          else this.counters.credits_decimal -= amount * -1

        // Check vendor name
        } else if (type === 'vendors') {
          matchedData = (this.allCodes[type] || []).find((data) => data.name === rawField) || false

        // if data is in our cache (allCodes), compare it
        } else if (this.allCodes.hasOwnProperty(type)) {
          // If not matched here, make matchedData false to trip error-checking
          matchedData = (this.allCodes[type] || []).find((data) => data.code === rawField && data.postable !== false) || false

        // All other cases
        } else {
          // TODO scrub data
          matchedData = rawField
        }

        return matchedData
      },
      // Show errors on HOT instance
      showCellErrors () {
        this.hotObject.updateSettings({
          cell: this.cellErrors
        })
      },
      // Convert our validated lines into Sigma Distribution line object
      convertToLineObject () {
        this.convertedObject = []
        for (let lineNum = 0; lineNum < this.validatedLines.length; lineNum++) {
          let line = this.validatedLines[lineNum]
          let newline = { form: {}, line_number: lineNum + 1 }
          for (let field in line) {
            let fieldName = this.fieldProperties[field].sigmaName

            if (!line[field]) {
              continue
            } else if (field === 'asset') {
              let asset = (typeof line[field] === 'string') ? line[field].toLowerCase() : line[field]

              switch (asset) {
                case null:
                case false:
                case 'no':
                case '':
                  newline.form[fieldName] = false
                  break

                case true:
                case 'yes':
                case 'x':
                  newline.form[fieldName] = true
                  break

                default:
                  newline.form[fieldName] = false
              }
            } else if (field === 'amount') {
              newline.form[fieldName] = parseFloat(line[field]).toFixed(2)
            } else if (line[field] && typeof line[field] === 'object') {
              newline.form[fieldName] = {}
              newline.form[fieldName].label = line[field].code || ''
              newline.form[fieldName].value = line[field].id || ''
            } else {
              // At this stage, if this.allCodes.hasOwnProperty(field), then line[field] is undefined and therefore
              // needs to be given a blank field object of { label: '', value: '' } to avoid import errors
              newline.form[fieldName] = this.allCodes.hasOwnProperty(field) ? { label: '', value: '' } : line[field]
            }
          }
          this.convertedObject.push(newline)
        }
      },
      acceptValidatedLines () {
        this.validated = false
        setTimeout(() => {
          if (this.validatedLines.length === 0) {
            this.createDialog('Please paste in data and select "Validate" before accepting.')
          } else if (this.convertedObject.length === 0) {
            this.createDialog('Please click on "Validate" before accepting.')
            return false
          } else {
            this.showDialog = false   // close all dialogs
            this.$notify.close()
            this.selectedImport === 'add' ? this.$emit('import', this.convertedObject, true) : this.$emit('import', this.convertedObject)
          }
        }, 500)
      }
    },
    mounted () {
      // Initialize the HOT object
      this.hotObject = this.createTable()
    },
    beforeUnload () {
      this.hotObject.destroy()
    }
  }
</script>

<style>
  .paste-header {
    margin-bottom: 20px;
  }
  .paste-buttons {
    text-align: right;
  }
  .paste-buttons ul {
    margin: 0 20px 0 0;
    padding: 0;
    list-style: none;
    display: inline-block;
    vertical-align: bottom;
  }
  .paste-buttons ul li {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
  }
  .paste-buttons button {
    margin-right: 5px;
  }
  .cell-error {
    font-weight: bold;
    color: red;
    border: solid 1px red;
    background-color: #ffff0050 !important;
  }
  #hot-dialog {
    border: solid red 1px;
  }
  .modified {
    background-color: #E6FFE6 !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/HotPaste.vue